/**
 * Shared between the consent widget and the cookie banner: the visual language
 * (rail, ledger rows, square switches, buttons), the display settings a
 * visitor can change (text size, contrast, motion, spacing), and the icons.
 *
 * One module so the two surfaces cannot drift apart. Display settings are kept
 * in this browser only and are never sent anywhere.
 */

export var A11Y_KEY = 'wk_a11y';
export var SIZES = [14, 16, 18, 21];

export var ICONS = {
  clock: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>',
  share: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="6" cy="12" r="2.2"/><circle cx="17" cy="6" r="2.2"/><circle cx="17" cy="18" r="2.2"/><path d="m8 11 7-4M8 13l7 4"/></svg>',
  scale: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4v16M5 20h14M7 7l-3 7h6zM17 7l-3 7h6zM4 7h16"/></svg>',
  person: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="4.5" r="2"/><path d="M4 9.5h16M12 9.5v5M12 14.5l-3.5 6M12 14.5l3.5 6"/></svg>',
  lang: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 5h9M7.5 3v2M4.5 20c3-2.5 5.5-6.5 6.5-10M9.5 20c-1.5-1.5-3-3.5-4-6"/><path d="M13 20l4-9 4 9M14.3 17h5.4"/></svg>',
  close: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>',
  chevron: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>',
  arrow: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>',
  lock: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>',
  shield: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z"/><path d="M9 12l2 2 4-4"/></svg>',
  check: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12.5l5 5L20 7"/></svg>',
  cookie: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3a9 9 0 1 0 9 9c-2.5.3-4.5-1.2-4.7-3.6C13.9 8.3 12.4 6 12 3z"/><circle cx="9" cy="10" r="1"/><circle cx="14" cy="15" r="1"/><circle cx="8.5" cy="15.5" r="1"/></svg>',
};

export function svg(name) {
  var wrap = document.createElement('span');
  wrap.innerHTML = ICONS[name];
  return wrap.firstChild;
}

export var DISPLAY_STRINGS = {
  en: {
    accessibility: 'Accessibility', display: 'Display', textSize: 'Text size', highContrast: 'High contrast',
    reduceMotion: 'Reduce motion', textSpacing: 'More text spacing', language: 'Language', close: 'Close',
    consentLabel: 'Consent', alwaysOn: 'Always on', poweredBy: 'Data protection and consent by',
  },
  hi: {
    accessibility: 'सुगम्यता', display: 'प्रदर्शन', textSize: 'अक्षर का आकार', highContrast: 'उच्च कंट्रास्ट',
    reduceMotion: 'गति कम करें', textSpacing: 'अधिक अक्षर-अंतर', language: 'भाषा', close: 'बंद करें',
    consentLabel: 'सहमति', alwaysOn: 'हमेशा चालू', poweredBy: 'डेटा सुरक्षा और सहमति',
  },
  // Draft translation, 1 Oct 2026. Needs review by a native speaker before production use.
  mr: {
    accessibility: 'सुलभता',
    display: 'प्रदर्शन',
    textSize: 'अक्षरांचा आकार',
    highContrast: 'उच्च कॉन्ट्रास्ट',
    reduceMotion: 'हालचाल कमी करा',
    textSpacing: 'अक्षरांमध्ये अधिक अंतर',
    language: 'भाषा',
    close: 'बंद करा',
    consentLabel: 'संमती',
    alwaysOn: 'नेहमी चालू',
    poweredBy: 'डेटा संरक्षण आणि संमती',
  },
  // Draft translation, 1 Oct 2026. Needs review by a native speaker before production use.
  gu: {
    accessibility: 'સુલભતા',
    display: 'પ્રદર્શન',
    textSize: 'અક્ષરનું કદ',
    highContrast: 'ઉચ્ચ કૉન્ટ્રાસ્ટ',
    reduceMotion: 'ગતિ ઓછી કરો',
    textSpacing: 'અક્ષરો વચ્ચે વધુ અંતર',
    language: 'ભાષા',
    close: 'બંધ કરો',
    consentLabel: 'સંમતિ',
    alwaysOn: 'હંમેશાં ચાલુ',
    poweredBy: 'ડેટા સંરક્ષણ અને સંમતિ',
  },
  // Draft translation, 1 Oct 2026. Needs review by a native speaker before production use.
  ta: {
    accessibility: 'அணுகல்தன்மை',
    display: 'காட்சி',
    textSize: 'எழுத்தின் அளவு',
    highContrast: 'உயர் மாறுபாடு',
    reduceMotion: 'அசைவைக் குறைக்கவும்',
    textSpacing: 'எழுத்துகளுக்கு இடையே கூடுதல் இடைவெளி',
    language: 'மொழி',
    close: 'மூடு',
    consentLabel: 'ஒப்புதல்',
    alwaysOn: 'எப்போதும் இயக்கத்தில்',
    poweredBy: 'தரவுப் பாதுகாப்பு மற்றும் ஒப்புதல்',
  },
  // Draft translation, 1 Oct 2026. Needs review by a native speaker before production use.
  te: {
    accessibility: 'సులభ వినియోగం',
    display: 'ప్రదర్శన',
    textSize: 'అక్షరాల పరిమాణం',
    highContrast: 'అధిక కాంట్రాస్ట్',
    reduceMotion: 'కదలికను తగ్గించండి',
    textSpacing: 'అక్షరాల మధ్య ఎక్కువ ఖాళీ',
    language: 'భాష',
    close: 'మూసివేయండి',
    consentLabel: 'సమ్మతి',
    alwaysOn: 'ఎల్లప్పుడూ ఆన్‌లో',
    poweredBy: 'డేటా పరిరక్షణ మరియు సమ్మతి',
  },
  // Draft translation, 1 Oct 2026. Needs review by a native speaker before production use.
  bn: {
    accessibility: 'অভিগম্যতা',
    display: 'প্রদর্শন',
    textSize: 'লেখার আকার',
    highContrast: 'উচ্চ কনট্রাস্ট',
    reduceMotion: 'গতি কমান',
    textSpacing: 'অক্ষরের মধ্যে বেশি ফাঁক',
    language: 'ভাষা',
    close: 'বন্ধ করুন',
    consentLabel: 'সম্মতি',
    alwaysOn: 'সর্বদা চালু',
    poweredBy: 'ডেটা সুরক্ষা ও সম্মতি',
  },
  // Draft translation, 1 Oct 2026. Needs review by a native speaker before production use.
  kn: {
    accessibility: 'ಪ್ರವೇಶಸಾಧ್ಯತೆ',
    display: 'ಪ್ರದರ್ಶನ',
    textSize: 'ಅಕ್ಷರದ ಗಾತ್ರ',
    highContrast: 'ಹೆಚ್ಚಿನ ಕಾಂಟ್ರಾಸ್ಟ್',
    reduceMotion: 'ಚಲನೆಯನ್ನು ಕಡಿಮೆ ಮಾಡಿ',
    textSpacing: 'ಅಕ್ಷರಗಳ ನಡುವೆ ಹೆಚ್ಚು ಅಂತರ',
    language: 'ಭಾಷೆ',
    close: 'ಮುಚ್ಚಿ',
    consentLabel: 'ಸಮ್ಮತಿ',
    alwaysOn: 'ಯಾವಾಗಲೂ ಆನ್',
    poweredBy: 'ಡೇಟಾ ಸಂರಕ್ಷಣೆ ಮತ್ತು ಸಮ್ಮತಿ',
  },
  // Draft translation, 1 Oct 2026. Needs review by a native speaker before production use.
  ml: {
    accessibility: 'പ്രവേശനക്ഷമത',
    display: 'പ്രദർശനം',
    textSize: 'അക്ഷരവലിപ്പം',
    highContrast: 'ഉയർന്ന കോൺട്രാസ്റ്റ്',
    reduceMotion: 'ചലനം കുറയ്ക്കുക',
    textSpacing: 'അക്ഷരങ്ങൾക്കിടയിൽ കൂടുതൽ അകലം',
    language: 'ഭാഷ',
    close: 'അടയ്ക്കുക',
    consentLabel: 'സമ്മതം',
    alwaysOn: 'എപ്പോഴും ഓൺ',
    poweredBy: 'ഡാറ്റ സംരക്ഷണവും സമ്മതവും',
  },
};

export function dt(lang, key) {
  return (DISPLAY_STRINGS[lang] && DISPLAY_STRINGS[lang][key]) || DISPLAY_STRINGS.en[key] || key;
}

export function loadA11y() {
  try { return JSON.parse(localStorage.getItem(A11Y_KEY) || '{}') || {}; } catch (e) { return {}; }
}
export function saveA11y(a) {
  try { localStorage.setItem(A11Y_KEY, JSON.stringify(a)); } catch (e) { /* private mode */ }
}

/** Applies the settings to a card element as custom properties and classes. */
export function applyA11y(card, a) {
  if (!card) return;
  a = a || {};
  var size = SIZES[a.size || 0] || SIZES[0];
  card.style.setProperty('--wk-size', size + 'px');
  card.classList.toggle('wk-a11y-contrast', !!a.contrast);
  card.classList.toggle('wk-a11y-motion', !!a.motion);
  card.classList.toggle('wk-a11y-spacing', !!a.spacing);
}

/**
 * The display strip: text size, contrast, motion, spacing. `h` is the caller's
 * element helper, `a` the settings object it owns, `onChange` re-applies them.
 * `save` persists them; the cookie banner's preview passes a no-op so a
 * preview never writes to the browser.
 */
export function displayStrip(h, lang, a, onChange, save) {
  var persist = save || saveA11y;
  var opt = function (key, label) {
    return h('label', { class: 'wk-opt' }, [
      h('input', {
        type: 'checkbox', checked: a[key] ? true : null,
        onchange: function (e) { a[key] = e.target.checked; persist(a); onChange(); },
      }),
      h('span', { text: label }),
    ]);
  };
  return h('div', { class: 'wk-strip', role: 'group', 'aria-label': dt(lang, 'display') }, [
    h('p', { class: 'wk-strip-title', text: dt(lang, 'textSize') }),
    h('div', { class: 'wk-sizes', role: 'group' }, SIZES.map(function (px, i) {
      return h('button', {
        class: 'wk-size', type: 'button', text: 'A', 'aria-label': dt(lang, 'textSize') + ' ' + (i + 1),
        'aria-pressed': (a.size || 0) === i ? 'true' : 'false', style: 'font-size:' + (12 + i * 2) + 'px',
        onclick: function (e) {
          a.size = i; persist(a); onChange();
          Array.prototype.forEach.call(e.currentTarget.parentNode.querySelectorAll('.wk-size'), function (b, j) {
            b.setAttribute('aria-pressed', j === i ? 'true' : 'false');
          });
        },
      });
    })),
    h('div', { class: 'wk-opts' }, [
      opt('contrast', dt(lang, 'highContrast')),
      opt('motion', dt(lang, 'reduceMotion')),
      opt('spacing', dt(lang, 'textSpacing')),
    ]),
  ]);
}

/** The language strip: each language named in its own script. */
export function langStrip(h, lang, offered, onPick) {
  return h('div', { class: 'wk-strip', role: 'group', 'aria-label': dt(lang, 'language') }, [
    h('p', { class: 'wk-strip-title', text: dt(lang, 'language') }),
    h('div', { class: 'wk-langs' }, offered.map(function (l) {
      return h('button', {
        class: 'wk-langopt', type: 'button', text: l.endonym, lang: l.code,
        'aria-current': l.code === lang ? 'true' : 'false',
        onclick: function () { onPick(l.code); },
      });
    })),
  ]);
}

/** A rail icon button. */
export function railButton(h, icon, label, onclick, cls) {
  var b = h('button', {
    class: 'wk-icon' + (cls ? ' ' + cls : ''), type: 'button', 'aria-label': label, title: label,
    'aria-expanded': 'false', onclick: onclick,
  });
  b.appendChild(svg(icon));
  return b;
}

/** The "always on" mark for a purpose that takes no decision. */
export function alwaysOn(h, lang, title) {
  var el = h('span', { class: 'wk-always', title: title || null });
  el.appendChild(svg('lock'));
  el.appendChild(document.createTextNode(dt(lang, 'alwaysOn')));
  return el;
}

/** The footer line that says whose record this is. */
export function poweredBy(h, lang) {
  var kept = h('span', { class: 'wk-kept' });
  kept.appendChild(svg('check'));
  kept.appendChild(document.createTextNode(dt(lang, 'poweredBy') + ' Wekalp'));
  return kept;
}

/**
 * Styles shared by both surfaces. The card itself (`.wk`) positions and sizes
 * differently on each, so it is declared by the caller; everything inside it
 * comes from here.
 */
export var SHARED_CSS = [
  '*,*::before,*::after{box-sizing:border-box}',
  '.wk *{font-family:inherit}',
  /* The rail. Dark, so the controls read as furniture rather than choices. */
  '.wk-rail{background:var(--wk-brand,#1C3D73);color:#fff;display:flex;flex-direction:column;align-items:center;padding:10px 0;gap:6px}',
  '.wk-rail-mark{width:28px;height:28px;border-radius:6px;background:rgba(255,255,255,.14);display:flex;align-items:center;justify-content:center;margin-bottom:6px}',
  '.wk-rail-mark svg{width:16px;height:16px;fill:none;stroke:#fff;stroke-width:1.8;stroke-linecap:round;stroke-linejoin:round}',
  '.wk-icon{width:36px;height:36px;border-radius:8px;border:0;background:transparent;color:#fff;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;padding:0;font:inherit;opacity:.85}',
  '.wk-icon svg{width:18px;height:18px;fill:none;stroke:currentColor;stroke-width:1.8;stroke-linecap:round;stroke-linejoin:round}',
  '.wk-icon:hover{opacity:1;background:rgba(255,255,255,.14)}',
  '.wk-icon[aria-expanded="true"]{opacity:1;background:#fff;color:var(--wk-brand,#1C3D73)}',
  '.wk-icon:focus-visible{outline:2px solid #fff;outline-offset:-3px}',
  '.wk-icon-close{margin-top:auto}',
  /* The body column. */
  '.wk-main{display:flex;flex-direction:column;min-height:0;min-width:0}',
  '.wk-eyebrow{display:flex;align-items:baseline;gap:8px;padding:12px 16px 0;font-size:.74em;letter-spacing:.08em;text-transform:uppercase;color:var(--wk-subtle,#6B7280);font-weight:600}',
  '.wk-eyebrow b{color:var(--wk-text,#101828);font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0}',
  '.wk-body{padding:6px 16px 4px;min-height:0}',
  '.wk-title{font-size:1.25em;font-weight:600;margin:0 0 4px;line-height:1.3}',
  '.wk-title:focus{outline:none}',
  '.wk-intro{color:var(--wk-muted,#4B5563);margin:0 0 4px;font-size:.95em}',
  '.wk-note{color:var(--wk-subtle,#6B7280);font-size:.85em;margin:8px 0 0}',
  '.wk-count{font-size:.85em;color:var(--wk-muted,#4B5563);margin:0 0 6px;font-variant-numeric:tabular-nums}',
  /* Purpose rows: a ledger, not a stack of cards. */
  '.wk-cats{border-top:1px solid var(--wk-border,#E4E8EF);margin-top:6px}',
  '.wk-cat{border-bottom:1px solid var(--wk-border,#E4E8EF)}',
  '.wk-cat-row{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:6px 14px;padding:12px 0}',
  '.wk-cat-name{font-weight:600;display:block;font-size:1em}',
  '.wk-cat-desc{display:block;color:var(--wk-muted,#4B5563);font-size:.9em;margin-top:2px}',
  '.wk-cat-meta{display:flex;flex-wrap:wrap;gap:4px 12px;align-items:center;margin-top:6px;font-size:.84em;color:var(--wk-subtle,#6B7280)}',
  '.wk-sep{color:var(--wk-border,#C9CEDF)}',
  '@media (max-width:480px){.wk-cat-meta{flex-direction:column;align-items:flex-start;gap:2px}.wk-sep{display:none}}',
  '.wk-always{display:inline-flex;align-items:center;gap:6px;font-size:.78em;font-weight:600;letter-spacing:.04em;color:var(--wk-ok,#0F766E);white-space:nowrap;padding-top:4px}',
  '.wk-always svg{width:14px;height:14px;fill:none;stroke:currentColor;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}',
  '.wk-more{font:inherit;font-size:inherit;color:var(--wk-brand,#1C3D73);background:none;border:0;padding:0;cursor:pointer;display:inline-flex;align-items:center;gap:4px;text-decoration:underline;text-underline-offset:2px}',
  '.wk-more svg{width:13px;height:13px;fill:none;stroke:currentColor;stroke-width:2;transition:transform .18s ease}',
  '.wk-more[aria-expanded="true"] svg{transform:rotate(180deg)}',
  '.wk-more:focus-visible{outline:2px solid var(--wk-focus,#2563EB);outline-offset:2px;border-radius:3px}',
  /* A real checkbox, visually a square switch: keyboard, screen readers and forms keep working. */
  '.wk-switch{position:relative;flex:none;width:42px;height:24px;margin-top:2px}',
  '.wk-switch input{position:absolute;inset:0;width:100%;height:100%;margin:0;opacity:0;cursor:pointer}',
  '.wk-track{position:absolute;inset:0;border-radius:6px;background:var(--wk-off,#CDD3DE);box-shadow:inset 0 0 0 1px rgba(16,24,40,.06);transition:background .18s ease;pointer-events:none}',
  '.wk-knob{position:absolute;top:3px;left:3px;width:18px;height:18px;border-radius:4px;background:#fff;transition:transform .18s ease;pointer-events:none;box-shadow:0 1px 2px rgba(16,24,40,.28)}',
  '.wk-switch input:checked~.wk-track{background:var(--wk-brand,#1C3D73);box-shadow:none}',
  '.wk-switch input:checked~.wk-knob{transform:translateX(18px)}',
  '.wk-switch:hover .wk-track{filter:brightness(.97)}',
  '.wk-switch input:focus-visible~.wk-track{outline:2px solid var(--wk-focus,#2563EB);outline-offset:3px}',
  /* Actions. Rejecting and accepting are the same button in every respect. */
  '.wk-actions{display:flex;flex-direction:column;gap:8px;padding:12px 16px 6px}',
  '.wk-pair{display:flex;gap:8px}',
  '.wk-btn{flex:1;min-width:0;font:inherit;font-size:.95em;font-weight:600;border-radius:6px;padding:11px 10px;cursor:pointer;border:1.5px solid var(--wk-brand,#1C3D73);background:var(--wk-surface,#fff);color:var(--wk-brand,#1C3D73);white-space:nowrap}',
  '.wk-btn:hover{background:var(--wk-tint,#EAF1FF)}',
  '.wk-btn:focus-visible{outline:2px solid var(--wk-focus,#2563EB);outline-offset:3px}',
  '.wk-btn-primary{background:var(--wk-brand,#1C3D73);color:#fff}',
  '.wk-btn-primary:hover{background:var(--wk-brand-hover,#254C8A);border-color:var(--wk-brand-hover,#254C8A)}',
  '.wk-btn[disabled]{opacity:.6;cursor:progress}',
  '.wk-textact{align-self:flex-start;font:inherit;font-size:.92em;font-weight:600;color:var(--wk-brand,#1C3D73);background:none;border:0;padding:4px 0;cursor:pointer;display:inline-flex;align-items:center;gap:6px}',
  '.wk-textact svg{width:14px;height:14px;fill:none;stroke:currentColor;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}',
  '.wk-textact:focus-visible{outline:2px solid var(--wk-focus,#2563EB);outline-offset:2px;border-radius:3px}',
  '.wk-foot{display:flex;flex-wrap:wrap;gap:4px 14px;align-items:center;justify-content:space-between;padding:6px 16px 12px;font-size:.78em;color:var(--wk-subtle,#6B7280)}',
  '.wk-link{color:var(--wk-brand,#1C3D73);text-decoration:underline;text-underline-offset:2px;background:none;border:0;padding:0;font:inherit;cursor:pointer}',
  '.wk-link:focus-visible{outline:2px solid var(--wk-focus,#2563EB);outline-offset:2px}',
  '.wk-kept{display:inline-flex;align-items:center;gap:5px;white-space:nowrap}',
  '.wk-kept svg{width:13px;height:13px;fill:none;stroke:var(--wk-ok,#0F766E);stroke-width:2;stroke-linecap:round;stroke-linejoin:round}',
  '.wk-alert{margin:0 16px 8px;padding:9px 12px;border-radius:6px;font-size:.9em}',
  '.wk-alert-err{background:#FDF4F4;border:1px solid #E7C2C0;color:#B3261E}',
  '.wk-alert-ok{background:#F1F8F4;border:1px solid #BEDCC9;color:#0F766E}',
  /* Display and language strips, under the eyebrow. */
  '.wk-strip{margin:8px 16px 0;padding:10px 12px;border:1px solid var(--wk-border,#D9DEE7);border-radius:6px;background:var(--wk-card,#F8F9FB)}',
  '.wk-strip-title{font-size:.72em;letter-spacing:.1em;text-transform:uppercase;color:var(--wk-subtle,#6B7280);font-weight:600;margin:0 0 8px}',
  '.wk-sizes{display:flex;gap:6px;margin-bottom:8px}',
  '.wk-size{flex:1;font:inherit;font-weight:700;border:1px solid var(--wk-border,#D9DEE7);border-radius:6px;background:var(--wk-surface,#fff);color:var(--wk-text,#101828);cursor:pointer;height:36px}',
  '.wk-size[aria-pressed="true"]{background:var(--wk-brand,#1C3D73);border-color:var(--wk-brand,#1C3D73);color:#fff}',
  '.wk-size:focus-visible,.wk-langopt:focus-visible{outline:2px solid var(--wk-focus,#2563EB);outline-offset:2px}',
  '.wk-opts{display:flex;flex-wrap:wrap;gap:6px}',
  '.wk-opt{display:inline-flex;align-items:center;gap:8px;font-size:.9em;border:1px solid var(--wk-border,#D9DEE7);border-radius:999px;padding:5px 12px 5px 8px;background:var(--wk-surface,#fff);cursor:pointer}',
  '.wk-opt input{margin:0;width:16px;height:16px;accent-color:var(--wk-brand,#1C3D73)}',
  '.wk-opt:has(input:checked){border-color:var(--wk-brand,#1C3D73);background:var(--wk-tint,#EAF1FF)}',
  '.wk-langs{display:flex;flex-wrap:wrap;gap:6px}',
  '.wk-langopt{font:inherit;font-size:.9em;border:1px solid var(--wk-border,#D9DEE7);background:var(--wk-surface,#fff);padding:5px 12px;border-radius:999px;cursor:pointer;color:var(--wk-text,#101828)}',
  '.wk-langopt:hover{background:var(--wk-tint,#EAF1FF)}',
  '.wk-langopt[aria-current="true"]{font-weight:600;color:#fff;background:var(--wk-brand,#1C3D73);border-color:var(--wk-brand,#1C3D73)}',
  /* Display settings, applied as classes on the card. */
  '.wk.wk-a11y-contrast{--wk-text:#000;--wk-muted:#111;--wk-subtle:#222;--wk-border:#000;--wk-brand:#0000C8;--wk-brand-hover:#000090;--wk-tint:#E6E6FF;--wk-card:#fff;--wk-off:#666;--wk-surface:#fff;--wk-focus:#C60000;--wk-ok:#005A2B}',
  '.wk.wk-a11y-contrast .wk-btn{border-width:2px}',
  '.wk.wk-a11y-spacing{--wk-tracking:.06em;--wk-wordsp:.12em;line-height:1.85}',
  '.wk.wk-a11y-motion *{transition:none!important;animation:none!important}',
  '@media (prefers-reduced-motion:reduce){.wk *{transition:none!important}}',
  /* Right to left: Urdu, Kashmiri and Sindhi. The knob travels the other way. */
  '.wk[dir="rtl"] .wk-knob{left:auto;right:3px}',
  '.wk[dir="rtl"] .wk-switch input:checked~.wk-knob{transform:translateX(-18px)}',
].join('');
